<template>
  <div class="card choice-card">
    <div class="card-header d-flex align-items-center">
      <span class="required">{{ title }}</span>
      <button
        v-if="exchange"
        class="btn btn-sm btn-success ms-auto"
        @click.prevent="emit('show-select-exchange')"
      >
        {{ $t('application_form.change_choice') }}
      </button>
    </div>
    <div class="card-body">
      <div v-if="exchange">
        <div
          v-if="hasErrorMessages"
          class="alert alert-danger"
        >
          <h4>{{ $t('application_form.errors_title') }}</h4>
          <hr>
          <ul>
            <li
              v-for="(message, messageIdx) of exchange.errorMessages"
              :key="messageIdx"
            >
              {{ message }}
            </li>
          </ul>
        </div>
        <div class="row gx-2 exchange-details">
          <dl class="col col-6">
            <dt>{{ $t('long_term_application.choice.institution_name') }}</dt>
            <dd>{{ exchange.institutionName }}</dd>
          </dl>
          <dl class="col col-3">
            <dt>{{ $t('long_term_application.choice.available_places') }}</dt>
            <dd>{{ exchange.availablePlaces }}</dd>
          </dl>
          <dl class="col col-3">
            <dt>{{ $t('long_term_application.choice.functions') }}</dt>
            <dd>{{ exchange.functions }}</dd>
          </dl>
        </div>
        <hr>
        <div class="row gx-2">
          <div class="mb-3 col-3">
            <label class="form-label required">{{ $t('application_form.choice.duration') }}</label>
            <select
              v-model="exchange.durationId"
              class="form-select form-select-sm"
            >
              <option
                v-for="duration of exchange.durationItems"
                :key="duration.id"
                :value="duration.id"
              >
                {{ duration.name }}
              </option>
            </select>
          </div>
        </div>
        <div
          v-if="isCJEU && hasCjeuAllocations"
          class="row"
        >
          <div class="mb-3 col">
            <label class="form-label required">{{ $t('long_term_application.choice.select_cjeu_allocation') }}</label>
            <select
              v-model="exchange.cjeuAllocationId"
              class="form-select form-select-sm"
            >
              <option/>
              <option
                v-for="cjeuAllocation of exchange.cjeuAllocationItems"
                :key="cjeuAllocation.id"
                :value="cjeuAllocation.id"
              >
                {{ cjeuAllocation.name }}
              </option>
            </select>
            <small class="form-text">{{ $t('long_term_application.choice.select_cjeu_allocation_note') }}</small>
          </div>
        </div>
        <hr>
        <div>
          <label class="form-label">{{ $t('long_term_application.choice.special_interest') }}</label>
          <input
            v-model="exchange.specialInterest"
            class="form-control form-control-sm"
            type="text"
          >
        </div>
      </div>
      <div v-else>
        <p class="card-text text-muted text-center"><em>{{ $t('long_term_application.choice_not_set_required') }}</em>
        </p>
        <div class="text-center">
          <button
            class="btn btn-sm btn-info"
            type="button"
            @click.prevent="emit('show-select-exchange')"
          >
            {{ $t('long_term_application.add_choice') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed} from 'vue'
import {useTranslation} from 'i18next-vue'
import type {LongTermExchangeChoice} from '../types'

const {t: $t} = useTranslation()

defineProps({
  title: {required: true, type: String},
})

const exchange = defineModel<LongTermExchangeChoice | null>('exchange', {required: true})

const emit = defineEmits<{
  'show-select-exchange': []
}>()

const isCJEU = computed(() => exchange.value?.institutionTag === 'CJEU')

const hasCjeuAllocations = computed(() =>
  Array.isArray(exchange.value?.cjeuAllocationItems) &&
  exchange.value.cjeuAllocationItems.length > 0
)

const hasErrorMessages = computed(() =>
  Array.isArray(exchange.value?.errorMessages) &&
  exchange.value.errorMessages.length > 0
)
</script>
